import React, { useState, useCallback, useRef } from 'react'

interface Props {
	children: React.ReactNode
	minHeight: number
	maxHeight?: number
	resizable?: boolean
	className?: string
	theme?: 'light' | 'dark'
}

const ResizableEditorContainer = ({
	children,
	minHeight,
	maxHeight = 800,
	resizable = true,
	className = '',
	theme = 'dark',
}: Props) => {
	const [currentHeight, setCurrentHeight] = useState(minHeight)
	const [isResizing, setIsResizing] = useState(false)
	const containerRef = useRef<HTMLDivElement>(null)

	// Handle mouse down on resize handle
	const handleMouseDown = useCallback(
		(e: React.MouseEvent) => {
			if (!resizable) return

			e.preventDefault()
			setIsResizing(true)

			const startY = e.clientY
			const startHeight = currentHeight

			const handleMouseMove = (moveEvent: MouseEvent) => {
				const deltaY = moveEvent.clientY - startY
				const newHeight = Math.max(minHeight, Math.min(maxHeight, startHeight + deltaY))
				setCurrentHeight(newHeight)
			}

			const handleMouseUp = () => {
				setIsResizing(false)
				document.removeEventListener('mousemove', handleMouseMove)
				document.removeEventListener('mouseup', handleMouseUp)
			}

			document.addEventListener('mousemove', handleMouseMove)
			document.addEventListener('mouseup', handleMouseUp)
		},
		[currentHeight, minHeight, maxHeight, resizable],
	)

	// Theme-based styling
	const getThemeStyles = () => {
		if (theme === 'light') {
			return {
				backgroundColor: '#ffffff',
				borderColor: '#d1d5db',
				resizeHandleColor: '#e5e7eb',
				resizeHandleBorder: '#d1d5db',
				dotColor: '#6b7280',
			}
		}
		return {
			backgroundColor: '#1e1e1e',
			borderColor: '#333',
			resizeHandleColor: '#333',
			resizeHandleBorder: '#555',
			dotColor: '#888',
		}
	}

	const themeStyles = getThemeStyles()

	return (
		<div
			ref={containerRef}
			className={`resizable-editor-container ${className}`}
			style={{
				border: `1px solid ${themeStyles.borderColor}`,
				borderRadius: '4px',
				backgroundColor: themeStyles.backgroundColor,
				height: `${currentHeight}px`,
				marginTop: '15px',
				display: 'flex',
				flexDirection: 'column',
				position: 'relative',
				overflow: 'hidden',
			}}
		>
			{/* Editor Content Area */}
			<div
				style={{
					flex: 1,
					overflow: 'auto',
					minHeight: 0, // Important for flex child to shrink
				}}
			>
				{children}
			</div>

			{/* Resize Handle */}
			{resizable && (
				<div
					onMouseDown={handleMouseDown}
					style={{
						height: '8px',
						backgroundColor: themeStyles.resizeHandleColor,
						cursor: 'ns-resize',
						borderTop: `1px solid ${themeStyles.resizeHandleBorder}`,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						opacity: isResizing ? 1 : 0.7,
						transition: 'opacity 0.2s ease',
						userSelect: 'none',
					}}
					onMouseEnter={(e) => {
						e.currentTarget.style.opacity = '1'
					}}
					onMouseLeave={(e) => {
						if (!isResizing) {
							e.currentTarget.style.opacity = '0.7'
						}
					}}
				>
					{/* Resize indicator dots */}
					<div style={{ display: 'flex', gap: '2px' }}>
						<div
							style={{
								width: '3px',
								height: '3px',
								backgroundColor: themeStyles.dotColor,
								borderRadius: '50%',
							}}
						/>
						<div
							style={{
								width: '3px',
								height: '3px',
								backgroundColor: themeStyles.dotColor,
								borderRadius: '50%',
							}}
						/>
						<div
							style={{
								width: '3px',
								height: '3px',
								backgroundColor: themeStyles.dotColor,
								borderRadius: '50%',
							}}
						/>
					</div>
				</div>
			)}
		</div>
	)
}

export default ResizableEditorContainer
